import { useCallback } from 'react'
import {
  LocalStorageKey,
  SetLocalStorageItem,
  useLocalStorageItem,
  useSetLocalStorageItem,
} from '../../../lib/utils/local-storage'
import { month } from '../../../lib/utils/time'

// The backend limit is 3000 usages per IP per month, but the actual limit is 5 usages per IP per month set on the frontend
const actualMaxCountSubmitAnonymous = 3000
export const maxCountSubmitAnonymous = 5
export const maxCountSubmitSignedIn = 100
export const maxCountSubmitPremium = 1000

export interface BackendPromptCap {
  promptsRemaining: number
  refillDate: string
}

const syncSignedInPromptCap = (
  { promptsRemaining, refillDate }: BackendPromptCap,
  setLocalStorageItem: SetLocalStorageItem
) => {
  // Some users, like Grammarly employees, can have > 100 max prompts, so Math.max prevents negative numbers here.
  // We should clean up and better sync the api prompt caps with the localStorage ones.
  const usedPrompts = Math.max(0, maxCountSubmitSignedIn - promptsRemaining)
  setLocalStorageItem(LocalStorageKey.countGoToolsSubmitSignedIn, usedPrompts.toString())
  setLocalStorageItem(
    LocalStorageKey.expirationTimestampGoToolsSubmitSignedIn,
    new Date(refillDate).getTime().toString()
  )
}

const syncAnonymousPromptCap = (
  { promptsRemaining, refillDate }: BackendPromptCap,
  setLocalStorageItem: SetLocalStorageItem
) => {
  const usedPrompts = Math.max(0, actualMaxCountSubmitAnonymous - promptsRemaining)
  setLocalStorageItem(LocalStorageKey.countGoToolsSubmitAnonymous, usedPrompts.toString())
  setLocalStorageItem(
    LocalStorageKey.expirationTimestampGoToolsSubmitAnonymous,
    new Date(refillDate).getTime().toString()
  )
}

export const useSyncBackendPromptCapToLocalStorage = (isAnonymous: boolean) => {
  const setLocalStorageItem = useSetLocalStorageItem()

  return useCallback(
    (backendPromptCap: BackendPromptCap) => {
      if (backendPromptCap) {
        if (!isAnonymous) {
          syncSignedInPromptCap(backendPromptCap, setLocalStorageItem)
        } else {
          syncAnonymousPromptCap(backendPromptCap, setLocalStorageItem)
        }
      }
    },
    [isAnonymous, setLocalStorageItem]
  )
}

export const useUpdateCountSubmit = (isAnonymous: boolean) => {
  const [expirationLocalStorageKey, expirationValue] = useExpirationTimestampFromLocalStorage(isAnonymous)
  const [countLocalStorageKey, countValue] = useCountSubmitFromLocalStorage(isAnonymous)
  const setLocalStorageItem = useSetLocalStorageItem()

  return () => {
    const dateNow = Date.now()
    if (dateNow > expirationValue) {
      setLocalStorageItem(expirationLocalStorageKey, (dateNow + month * 1000).toString())
      setLocalStorageItem(countLocalStorageKey, '1')
    } else {
      setLocalStorageItem(countLocalStorageKey, (countValue + 1).toString())
    }
  }
}

export const useExpirationTimestampFromLocalStorage = (isAnonymous: boolean): [LocalStorageKey, number] => {
  const localStorageKey = isAnonymous
    ? LocalStorageKey.expirationTimestampGoToolsSubmitAnonymous
    : LocalStorageKey.expirationTimestampGoToolsSubmitSignedIn
  const expiration = useLocalStorageItem(localStorageKey)
  const expirationFromLocalStorage = Number(expiration)
  return [localStorageKey, isNaN(expirationFromLocalStorage) ? 0 : expirationFromLocalStorage]
}

export const useCountSubmitFromLocalStorage = (isAnonymous: boolean): [LocalStorageKey, number] => {
  const localStorageKey = isAnonymous
    ? LocalStorageKey.countGoToolsSubmitAnonymous
    : LocalStorageKey.countGoToolsSubmitSignedIn
  const countSubmit = useLocalStorageItem(localStorageKey)
  const countFromLocalStorage = Number(countSubmit)
  return [localStorageKey, isNaN(countFromLocalStorage) ? 0 : countFromLocalStorage]
}

export const useHasPromptsRemaining = (isAnonymous: boolean, isPremium: boolean): boolean => {
  const [_, countSubmit] = useCountSubmitFromLocalStorage(isAnonymous)
  const maxCountSubmit = isAnonymous
    ? actualMaxCountSubmitAnonymous
    : isPremium
      ? maxCountSubmitPremium
      : maxCountSubmitSignedIn
  return countSubmit < maxCountSubmit
}
